import React, {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { Activity } from '../types';
import { WebChatActivity } from 'botframework-webchat-core';
import {
    TENANTS,
    TIME_SECONDS_LONGER_WAIT_THAN_USUAL,
    TIME_SECONDS_TRIGGER_TIMEOUT,
} from '../constants';
import { getTenantName } from '../helpers';
import { toEpochMs } from '../utils/conversationStatus';

// Timer for inactivity is handled elsewhere - TODO - centralize this?

export type ConversationStatusValue = {
    didTimeout: boolean;
    setDidTimeout: (v: boolean) => void;
    inactive: boolean;
    setInactive: React.Dispatch<React.SetStateAction<boolean>>;
    isUserWaiting: boolean;
    setIsUserWaiting: (v: boolean) => void;
    timeoutMessageId: string;
    setInteractionAnchors: (anchors: {
        lastMessage?: Activity;
        lastBotActivity?: WebChatActivity;
    }) => void;
    setSendEvent: (
        fn: ((name: string, value?: any) => void) | undefined
    ) => void;
};

const ConversationStatusContext = createContext<
    ConversationStatusValue | undefined
>(undefined);

export type ConversationStatusProviderProps = {
    children: React.ReactNode;
    initialDidTimeout?: boolean;
    initialInactive?: boolean;
};

export const ConversationStatusProvider: React.FC<
    ConversationStatusProviderProps
> = ({ children, initialDidTimeout = false, initialInactive = false }) => {
    const [didTimeout, setDidTimeout] = useState<boolean>(initialDidTimeout);
    const [inactive, setInactive] = useState<boolean>(initialInactive);
    const [isUserWaiting, setIsUserWaiting] = useState<boolean>(false);
    const [lastMessage, setLastMessage] = useState<Activity | undefined>(
        undefined
    );
    const [lastBotActivity, setLastBotActivity] = useState<
        WebChatActivity | undefined
    >(undefined);
    const [timeoutMessageId, setTimeoutMessageId] = useState<string>('');
    // Keep sendEvent in a ref so timer callbacks always see the latest handler
    // without adding it to effect dependencies. This avoids stale closures and
    // prevents unintended timer resets when setSendEvent is called.
    const sendEventRef = useRef<
        ((name: string, value?: any) => void) | undefined
    >(undefined);

    // Derived timestamps used to control timer reset behavior.
    const lastMessageTimestampMs = useMemo(
        () => toEpochMs(lastMessage),
        [lastMessage]
    );
    const lastBotActivityTimestampMs = useMemo(
        () => toEpochMs(lastBotActivity),
        [lastBotActivity]
    );

    // Provide context value to consumers
    const value = useMemo(
        () => ({
            didTimeout,
            setDidTimeout,
            inactive,
            setInactive,
            isUserWaiting,
            setIsUserWaiting,
            timeoutMessageId,
            setInteractionAnchors: (anchors: {
                lastMessage?: Activity;
                lastBotActivity?: WebChatActivity;
            }) => {
                const messageChanged =
                    lastMessage?.id !== anchors.lastMessage?.id;
                const botChanged =
                    lastBotActivity?.id !== anchors.lastBotActivity?.id;
                if (!messageChanged && !botChanged) return;
                if (messageChanged)
                    setLastMessage(anchors.lastMessage as Activity);
                if (botChanged)
                    setLastBotActivity(
                        anchors.lastBotActivity as WebChatActivity
                    );
            },
            setSendEvent: (
                fn: ((name: string, value?: any) => void) | undefined
            ) => {
                sendEventRef.current = fn;
            },
        }),
        [
            didTimeout,
            inactive,
            isUserWaiting,
            timeoutMessageId,
            lastMessage,
            lastBotActivity,
        ]
    );

    // Timers for 'longer than usual' and 'timeout'
    const longerRef = useRef<NodeJS.Timeout | null>(null);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Helper: clear both timers
    const clearAllTimers = () => {
        if (longerRef.current) {
            clearTimeout(longerRef.current);
            longerRef.current = null;
        }
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    };

    // Helper: start both timers with current thresholds
    const startTimers = () => {
        // Only start the longer-than-usual timer for the Message Assist tenant
        if (getTenantName() === TENANTS.MESSAGE_ASSIST) {
            longerRef.current = setTimeout(() => {
                // Emit a 'userWaiting' event via WebChat's sendEvent when longer-than-usual delay occurs
                // Read from the ref to use the current handler without restarting timers
                const sendEvent = sendEventRef.current;
                if (sendEvent && lastMessage) {
                    const ts =
                        (lastMessage as any)?.timestamp ||
                        (lastMessage as any)?.localTimestamp ||
                        new Date().toISOString();
                    const id = (lastMessage as any)?.id || '';
                    // NOTE: sendEvent relies on communication with directLine, so if there is an
                    // issue with directLine, this message will not appear.
                    // The timeout handled below though is all client-side, so it will still be surfaced.
                    sendEvent('userWaiting', {
                        from: 'bot',
                        timestamp: ts,
                        id: id,
                    });
                }
            }, TIME_SECONDS_LONGER_WAIT_THAN_USUAL * 1000);
        }

        timeoutRef.current = setTimeout(() => {
            setDidTimeout(true);
            setTimeoutMessageId(
                lastMessage ? (lastMessage as any)?.id || '' : ''
            );
        }, TIME_SECONDS_TRIGGER_TIMEOUT * 1000);
    };

    // Stable key based on the most recent observed interaction timestamp
    const lastInteractionKey = useMemo(() => {
        const a = lastMessageTimestampMs ?? Number.NEGATIVE_INFINITY;
        const b = lastBotActivityTimestampMs ?? Number.NEGATIVE_INFINITY;
        const maxTs = Math.max(a, b);
        return maxTs === Number.NEGATIVE_INFINITY ? null : String(maxTs);
    }, [lastMessageTimestampMs, lastBotActivityTimestampMs]);

    // Effect A: schedule/restart timers on waiting start or relevant last interaction changes
    // This effect is ONLY called when isUserWaiting or lastInteractionKey changes.
    useEffect(() => {
        const hasInteractionAnchor = lastInteractionKey !== null;

        if (!isUserWaiting || !hasInteractionAnchor) {
            // Not waiting: clear timers
            clearAllTimers();
            return;
        }

        // We're actively waiting: reset to full thresholds
        // Keeping this separate from the clearTimeout above
        // allows better tracing of state changes.
        clearAllTimers();
        startTimers();
        return () => {
            clearAllTimers();
        };
    }, [isUserWaiting, lastInteractionKey]);

    // Effect B: cancel timers during inactivity or other bot timeout
    useEffect(() => {
        if (inactive || didTimeout) {
            clearAllTimers();
        }
    }, [inactive, didTimeout]);

    return (
        <ConversationStatusContext.Provider value={value}>
            {children}
        </ConversationStatusContext.Provider>
    );
};

export const useConversationStatus = (): ConversationStatusValue => {
    const ctx = useContext(ConversationStatusContext);
    if (!ctx)
        throw new Error(
            'useConversationStatus must be used within a ConversationStatusProvider'
        );
    return ctx;
};
